import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Search, Trash2, CheckSquare, Square, UserPlus, ArrowUpDown } from 'lucide-react';
import { usePractitionerClients } from '@/hooks/usePractitionerClients';
import { usePractitionerSessions } from '@/hooks/usePractitionerSessions';
import { PractitionerClient } from '@/types/practitioner';
import { ClientForm } from './ClientForm';
import { EnhancedClientCard } from './EnhancedClientCard';
import LoadingSpinner from '@/components/LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';

export const ClientManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<PractitionerClient | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [selectedClients, setSelectedClients] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<'name' | 'recent' | 'sessions'>('name');
  const navigate = useNavigate();

  const { clients, loading, createClient, updateClient, deleteClient } = usePractitionerClients();
  const { clientSessions, sharedLinks, loading: sessionsLoading } = usePractitionerSessions();

  const filteredClients = clients.filter(client => {
    const displayName = `${client.first_name}${client.last_name ? ' ' + client.last_name : ''}`;
    return displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.phone?.includes(searchTerm);
  });

  // Enhanced client data with session information
  const enhancedClients = useMemo(() => {
    const enhanced = filteredClients.map(client => {
      const clientSessionsData = clientSessions.filter(s => s.client_id === client.id);
      const clientLinks = sharedLinks.filter(l => l.client_id === client.id);
      // Links never expire now, so just check if active
      const activeLinks = clientLinks.filter(l => l.is_active);
      const pendingLinks = activeLinks.filter(l => l.delivery_status === 'created' || l.delivery_status === 'sent');
      const lastSession = clientSessionsData.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];

      return {
        ...client,
        sessionCount: clientSessionsData.length,
        lastSessionDate: lastSession ? new Date(lastSession.created_at) : undefined,
        activeLinks: activeLinks.length,
        pendingLinks: pendingLinks.length,
      };
    });

    // Sort based on selected option
    return enhanced.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
        case 'recent':
          if (!a.lastSessionDate && !b.lastSessionDate) return 0;
          if (!a.lastSessionDate) return 1;
          if (!b.lastSessionDate) return -1;
          return b.lastSessionDate.getTime() - a.lastSessionDate.getTime();
        case 'sessions':
          return b.sessionCount - a.sessionCount;
        default:
          return 0;
      }
    });
  }, [filteredClients, clientSessions, sharedLinks, sortBy]);

  if (loading || sessionsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  const handleCreateClient = async (data: any) => {
    const success = await createClient(data);
    if (success) {
      setIsAddDialogOpen(false);
    }
  };

  const handleUpdateClient = async (data: any) => {
    if (!selectedClient) return;
    const success = await updateClient(selectedClient.id, data);
    if (success) {
      setIsEditDialogOpen(false);
      setSelectedClient(null);
    }
  };

  const handleDeleteClient = async (client: PractitionerClient) => {
    await deleteClient(client.id);
  };

  const handleCreateSession = (client: PractitionerClient) => {
    navigate('/practitioner/sessions', { state: { selectedClient: client } });
  };

  const toggleClientSelection = (clientId: string) => {
    const newSelected = new Set(selectedClients);
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId);
    } else {
      newSelected.add(clientId);
    }
    setSelectedClients(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedClients.size === enhancedClients.length) {
      setSelectedClients(new Set());
    } else {
      setSelectedClients(new Set(enhancedClients.map(c => c.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedClients.size === 0) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedClients.size} client${selectedClients.size > 1 ? 's' : ''}? This will deactivate their accounts but preserve session history.`
    );

    if (!confirmed) return;

    // Delete all selected clients
    for (const clientId of selectedClients) {
      await deleteClient(clientId);
    }

    setSelectedClients(new Set());
  };

  return (
    <div className="space-y-6">
      <div className="p-6 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
        <h1 className="text-3xl font-bold text-blue-900 dark:text-blue-100 mb-2">Client Management</h1>
        <p className="text-blue-700 dark:text-blue-300">
          Manage your clients and track their session activity
        </p>
      </div>

      <div className="p-4 bg-blue-50/50 dark:bg-blue-950/50 rounded-lg border border-blue-200/50 dark:border-blue-800/50 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex items-center gap-2">
            <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
              <SelectTrigger className="w-[180px]">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Sort by..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name (A-Z)</SelectItem>
                <SelectItem value="recent">Recent Activity</SelectItem>
                <SelectItem value="sessions">Session Count</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              onClick={() => setShowQuickAdd(!showQuickAdd)}
              className="border-blue-300 dark:border-blue-700"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Quick Add
            </Button>

            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Client
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader className="p-4 bg-blue-50 dark:bg-blue-950 rounded-t-lg border-b border-blue-200 dark:border-blue-800">
                  <DialogTitle className="text-blue-900 dark:text-blue-100">Add New Client</DialogTitle>
                </DialogHeader>
                <div className="p-4">
                  <ClientForm onSubmit={handleCreateClient} />
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {enhancedClients.length > 0 && (
          <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-900 rounded-lg border">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSelectAll}
                className="h-8 w-8 p-0"
              >
                {selectedClients.size === enhancedClients.length && enhancedClients.length > 0 ? (
                  <CheckSquare className="h-4 w-4 text-blue-600" />
                ) : (
                  <Square className="h-4 w-4" />
                )}
              </Button>
              <span className="text-sm text-muted-foreground">
                {selectedClients.size > 0 ? (
                  <Badge variant="secondary">{selectedClients.size} selected</Badge>
                ) : (
                  `${enhancedClients.length} client${enhancedClients.length !== 1 ? 's' : ''}`
                )}
              </span>
            </div>

            {selectedClients.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Selected
              </Button>
            )}
          </div>
        )}

        {/* Quick Add Form */}
        {showQuickAdd && (
          <Card className="border-blue-300 dark:border-blue-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Quick Add Client</CardTitle>
              <CardDescription>Add basic client info quickly</CardDescription>
            </CardHeader>
            <CardContent>
              <ClientForm
                onSubmit={async (data) => {
                  const success = await handleCreateClient(data);
                  if (success) {
                    setShowQuickAdd(false);
                  }
                }}
                compact={true}
              />
            </CardContent>
          </Card>
        )}
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {enhancedClients.length === 0 ? (
          <div className="col-span-full">
            <div className="text-center py-12 bg-blue-50/30 dark:bg-blue-950/30 rounded-lg border border-blue-200/30 dark:border-blue-800/30">
              <p className="text-blue-700 dark:text-blue-300 mb-4">
                {searchTerm ? 'No clients found matching your search.' : 'No clients found.'}
              </p>
              {!searchTerm && (
                <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Your First Client
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader className="p-4 bg-blue-50 dark:bg-blue-950 rounded-t-lg border-b border-blue-200 dark:border-blue-800">
                      <DialogTitle className="text-blue-900 dark:text-blue-100">Add New Client</DialogTitle>
                    </DialogHeader>
                    <div className="p-4">
                      <ClientForm onSubmit={handleCreateClient} />
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        ) : (
          enhancedClients.map((client) => (
            <div key={client.id} className="relative">
              {/* Selection Checkbox Overlay */}
              <div className="absolute top-3 left-3 z-10">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleClientSelection(client.id);
                  }}
                  className="h-6 w-6 p-0 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-800 rounded border"
                >
                  {selectedClients.has(client.id) ? (
                    <CheckSquare className="h-4 w-4 text-blue-600" />
                  ) : (
                    <Square className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <EnhancedClientCard
                client={client}
                sessionCount={client.sessionCount}
                lastSessionDate={client.lastSessionDate}
                activeLinks={client.activeLinks}
                pendingLinks={client.pendingLinks}
                onEdit={(client) => {
                  setSelectedClient(client);
                  setIsEditDialogOpen(true);
                }}
                onDelete={(client) => {
                  const displayName = `${client.first_name}${client.last_name ? ' ' + client.last_name : ''}`;
                  if (window.confirm(`Are you sure you want to remove ${displayName}? This will deactivate their account but preserve session history.`)) {
                    handleDeleteClient(client);
                  }
                }}
                onCreateSession={handleCreateSession}
              />
            </div>
          ))
        )}
      </div>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader className="p-4 bg-blue-50 dark:bg-blue-950 rounded-t-lg border-b border-blue-200 dark:border-blue-800">
            <DialogTitle className="text-blue-900 dark:text-blue-100">Edit Client</DialogTitle>
          </DialogHeader>
          <div className="p-4">
            {selectedClient && (
              <ClientForm
                initialData={selectedClient}
                onSubmit={handleUpdateClient}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};